import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { Delete, Info } from '@mui/icons-material'
import React from 'react'
import ReactPaginate from 'react-paginate'
import {
  deleteAuthorizationLog,
  getAuthorizationLogsByEpisodeOfCareId,
} from 'src/services/AuthorizationLogService'
import { AuthorizationLogSearchRequest } from 'src/viewModels/AuthorizationLogSearchRequest'
import { AuthorizationLogDTO } from 'src/viewModels/AuthorizationLogDTO'
import { pagedList } from 'src/viewModels/pagedList'
import moment from 'moment'
import Constants from '../config'
import { IUser } from '../Definitions'
import { IStores } from '../Stores'
import { inject, observer } from 'mobx-react'

interface IAuthorizationLogGridProps {
  episodeOfCareId: string
  user?: IUser
  sendNotification?: any
  onDataChanged?: () => void
}

@inject((stores: IStores) => ({
  user: stores.global.user,
  sendNotification: stores.notifications.sendNotification,
}))
@observer
class AuthorizationLogGrid extends React.Component<IAuthorizationLogGridProps> {
  public componentDidMount(): void {
    if (this.props.episodeOfCareId) {
      this.getAuthorizationLogData()
    }
  }

  public componentDidUpdate(prevProps: IAuthorizationLogGridProps): void {
    if (
      this.props.episodeOfCareId !== prevProps.episodeOfCareId &&
      this.props.episodeOfCareId
    ) {
      this.getAuthorizationLogData()
    }
  }

  private setPaginationOffsetData = () => {
    const { page, totalItems, pageSize } = this.state
    const firstRecordIndex = (page - 1) * pageSize + 1
    const lastRecordIndex = page * pageSize < totalItems ? page * pageSize : totalItems
    this.setState({
      firstRecordIndex: firstRecordIndex,
      lastRecordIndex: lastRecordIndex,
    })
  }

  private getAuthorizationLogData = async () => {
    const { page, pageSize } = this.state
    const data: AuthorizationLogSearchRequest = {
      episodeOfCareId: this.props.episodeOfCareId,
      page,
      pageSize,
    }
    this.setState({ isLoading: true })

    await getAuthorizationLogsByEpisodeOfCareId(data)
      .then((response: any) => {
        this.setState(
          {
            gridData: response.items,
            pageCount: response.totalPages,
            totalItems: response.totalItems,
          },
          () => {
            this.setPaginationOffsetData()
          }
        )
      })
      .catch((_) => {})
      .finally(() => this.setState({ isLoading: false }))
  }

  private deleteAuthorizationLog = (authorizationLogId: string) => {
    this.setState({
      isConfirmDialogOpen: true,
      deleteAuthorizationLogId: authorizationLogId,
    })
  }

  private handlePageChange = (event: any) => {
    const page = event.selected
    this.setState({ page: page + 1 }, () => this.getAuthorizationLogData())
  }

  private isGridDataEmpty = () => {
    return this.state.totalItems === 0
  }

  private mapLogGridData = () => {
    if (this.state.isLoading) {
      return (
        <TableRow className="gridNoPadding" key={1}>
          <TableCell colSpan={12} align="center">
            <CircularProgress size={50} />
          </TableCell>
        </TableRow>
      )
    }
    if (this.isGridDataEmpty()) {
      return (
        <TableRow className="gridNoPadding" key={1}>
          <TableCell colSpan={12} align="center">
            No records found
          </TableCell>
        </TableRow>
      )
    } else {
      return this.state.gridData.map((record, index) => (
        <TableRow className="gridPadding" key={index}>
          <TableCell>{moment(record.createdDate).format('MM/DD/YYYY')}</TableCell>
          <TableCell>{record.authorizationTypeString}</TableCell>
          <TableCell>
            {record.data?.trim().length > 0 ? (
              <Tooltip
                title={
                  <React.Fragment>
                    <span
                      style={{
                        fontSize: '18px',
                        lineHeight: '1.5em',
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {record.data}
                    </span>
                  </React.Fragment>
                }
              >
                <Info />
              </Tooltip>
            ) : (
              <></>
            )}
          </TableCell>
          <TableCell align="right">{record.otAndPtCount ?? ''}</TableCell>
          <TableCell style={{ paddingLeft: '32px' }}>
            {record.ptNote?.trim().length ? (
              <Tooltip
                title={
                  <React.Fragment>
                    <span
                      style={{
                        fontSize: '18px',
                        lineHeight: '1.5em',
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {record.ptNote}
                    </span>
                  </React.Fragment>
                }
              >
                <Info />
              </Tooltip>
            ) : (
              <></>
            )}
          </TableCell>
          <TableCell>{record.createdByName}</TableCell>
          <TableCell>
            {this.props.user?.roles.includes(Constants.ObxRoles.CareCoordinator) ||
            this.props.user?.roles.includes(Constants.ObxRoles.CareCoordinatorManager) ? (
              <Tooltip title="Delete Log Entry">
                <IconButton
                  size="small"
                  onClick={() => {
                    this.deleteAuthorizationLog(record.id)
                  }}
                >
                  <Delete style={{ color: 'red' }} />
                </IconButton>
              </Tooltip>
            ) : (
              ''
            )}
          </TableCell>
        </TableRow>
      ))
    }
  }

  private paginationInfo = () => {
    if (!this.isGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {this.state.firstRecordIndex} to {this.state.lastRecordIndex} of{' '}
            {this.state.totalItems} entries
          </Typography>
        </div>
      )
    } else {
      return <></>
    }
  }

  public state = {
    gridData: new pagedList().items as AuthorizationLogDTO[],
    page: 1,
    pageCount: 1,
    firstRecordIndex: 0,
    lastRecordIndex: 0,
    totalItems: 0,
    pageSize: 5,
    isConfirmDialogOpen: false,
    deleteAuthorizationLogId: '',
    isLoading: true,
  }

  public render() {
    return (
      <>
        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignContent="center"
          alignItems="flex-start"
          wrap="wrap"
        >
          <Grid item xs={12} style={{ minWidth: '750px' }}>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow className="gridPadding">
                  <TableCell>Created On</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell
                    align="right"
                    style={{ width: '140px', whiteSpace: 'nowrap' }}
                  >
                    OT &amp; PT Count
                  </TableCell>
                  <TableCell style={{ paddingLeft: '32px' }}>PT Note</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody style={{ width: '100%' }}>{this.mapLogGridData()}</TableBody>
            </Table>
            <div className="pagination-row">
              {this.paginationInfo()}
              <ReactPaginate
                previousLabel={'<'}
                nextLabel={'>'}
                onPageChange={this.handlePageChange}
                forcePage={this.state.page - 1}
                pageCount={this.state.pageCount}
                containerClassName={'pagination'}
                activeClassName={'active'}
                //@ts-ignore
                renderOnZeroPageCount={null}
              />
            </div>
          </Grid>
        </Grid>
        <Dialog open={this.state.isConfirmDialogOpen}>
          <DialogTitle>{<>Are you sure?</>}</DialogTitle>
          <DialogContent>Are you sure you want to delete this log entry?</DialogContent>
          <DialogActions>
            <Button
              data-cy="close"
              onClick={() =>
                this.setState({
                  isConfirmDialogOpen: false,
                  deleteAuthorizationLogId: '',
                })
              }
            >
              No
            </Button>
            <Button
              onClick={() =>
                deleteAuthorizationLog(this.state.deleteAuthorizationLogId)
                  .then(() =>
                    this.props.sendNotification('200', 'The log entry was deleted', {
                      variant: 'success',
                    })
                  )
                  .catch(() => {})
                  .finally(() => {
                    this.setState({
                      isConfirmDialogOpen: false,
                      deleteAuthorizationLogId: '',
                    })
                    const { page, pageSize, totalItems } = this.state
                    const estimatedItemsAfterDelete = totalItems - 1
                    const estimatedPagesAfterDelete = Math.ceil(
                      estimatedItemsAfterDelete / pageSize
                    )

                    if (
                      page > estimatedPagesAfterDelete &&
                      estimatedPagesAfterDelete > 0
                    ) {
                      this.setState({ page: estimatedPagesAfterDelete }, () => {
                        this.getAuthorizationLogData()
                        if (this.props.onDataChanged) {
                          this.props.onDataChanged()
                        }
                      })
                    } else if (estimatedItemsAfterDelete === 0) {
                      this.setState({ page: 1 }, () => {
                        this.getAuthorizationLogData()
                        if (this.props.onDataChanged) {
                          this.props.onDataChanged()
                        }
                      })
                    } else {
                      this.getAuthorizationLogData()
                      if (this.props.onDataChanged) {
                        this.props.onDataChanged()
                      }
                    }
                  })
              }
              data-cy="close"
              color="primary"
              variant="contained"
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }
}

export default AuthorizationLogGrid
